import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import ExperienceHeroSection from "../../components/UserComponents/ExperienceHeroSection";
import ExperienceInfo from "../../components/UserComponents/ExperienceInfo";
import ExperienceAvailability from "../../components/UserComponents/ExperienceAvailability";
import VendorInfo from "../../components/UserComponents/VendorInfo";
import ReviewBookingModal from "./ReviewBookingPage";
import GuestBookingModal from "../Guest/GuestBookingModal";
import ExperienceDetailsSkeleton from "../../components/common/ExperienceDetailsSkeleton";
import { toast } from "react-toastify";

import {
  usePublicExperienceById,
  useValidateBooking,
  useValidateBookingGuest,
} from "../../custom-hooks/queries";
import { useAuthStore } from "../../store/useAuthStore";
import { getCleanedUrl } from "../../utils/HelperMethod";

export default function ExperienceDetailsPage() {
  const { experienceId } = useParams();
  const token = useAuthStore.getState().token;

  const [selectedDate, setSelectedDate] = useState(null);
  const [people, setPeople] = useState(1);
  const [durationError, setDurationError] = useState(null);
  const [estimatedDuration, setEstimatedDuration] = useState(null);

  const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [selectedStartTime, setSelectedStartTime] = useState("");

  const { data, isLoading } = usePublicExperienceById(experienceId);
  const experience = data?.data || {};

  const vendorLogo = getCleanedUrl(experience?.vendor?.logoUrl);

  const durationResult = useMemo(() => {
    if (!people) {
      return { duration: null, error: null };
    }

    const rules = experience.escalationRules || [];

    if (rules.length === 0) {
      return {
        duration: experience.baseDurationMinutes,
        error: null,
      };
    }

    const matched = rules.find(
      (r) => people >= r.minGuests && people <= r.maxGuests,
    );

    if (!matched) {
      return {
        duration: null,
        error: "Group size not supported",
      };
    }

    return {
      duration: matched.durationMinutes,
      error: null,
    };
  }, [people, experience]);

  useEffect(() => {
    setEstimatedDuration(durationResult.duration);
    setDurationError(durationResult.error);
  }, [durationResult]);

  const {
    mutate: validateBooking,
    data: validationData,
    isPending: validating,
  } = useValidateBooking();

  const {
    mutate: validateBookingGuest,
    data: validationGuestData,
    isPending: validatingGuest,
  } = useValidateBookingGuest();

  /* =========================
     DURATION ESTIMATION
     ========================= */

  /* =========================
     BOOK NOW
     ========================= */

  const specificStartTime = experience?.specificStartTime;

  const handleBookNow = () => {
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    if (durationError) {
      toast.error(durationError);
      return;
    }

    const payload = {
      experienceId,
      selectedDate,
      nofPersons: people || null,
      // noOfGroups: experience.priceType === "PerGroup" ? people : null,
      addOnIds: selectedAddOnIds,
      selectedStartTime: specificStartTime ? selectedStartTime : null,
    };

    if (token) {
      validateBooking(payload, {
        onSuccess: () => setIsBookingModalOpen(true),
      });
    } else {
      setIsGuestModalOpen(true);
    }
  };

  if (isLoading) return <ExperienceDetailsSkeleton />;

  return (
    <div className="container mx-auto py-8 px-6">
      <PageHeader title="Experience" />

      <ExperienceHeroSection
        heroImage={experience.mainImage}
        galleryImages={experience.gallery || []}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-10">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-6">
          <ExperienceInfo
            title={experience.title}
            location={experience.location}
            price={experience.price}
            priceType={experience.priceType}
            description={experience.description}
            category={experience.category}
          />

          {experience.vendor && (
            <VendorInfo
              name={experience.vendor.businessName}
              description={experience.vendor.description}
              logoUrl={vendorLogo}
              rating={experience.vendor.ratingAvg}
              totalReviews={experience.vendor.totalReviews}
            />
          )}
        </div>

        {/* RIGHT */}
        <ExperienceAvailability
          calendarMeta={experience.calendarMeta}
          availabilityRules={experience.availability}
          people={people}
          setPeople={setPeople}
          estimatedDuration={estimatedDuration}
          durationError={durationError}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          addOns={experience.addsOn}
          selectedAddOnIds={selectedAddOnIds}
          setSelectedAddOnIds={setSelectedAddOnIds}
          onBookNow={handleBookNow}
          loading={validating}
          price={experience.price} // ✅ PASS HERE
          isConnected={experience.vendor?.isConnected}
          specificStartTime={experience.specificStartTime}
          startTimes={experience?.startTimes}
          selectedStartTime={selectedStartTime}
          setSelectedStartTime={setSelectedStartTime}
        />
      </div>

      {/* MODALS */}
      <GuestBookingModal
        isOpen={isGuestModalOpen}
        onClose={() => setIsGuestModalOpen(false)}
        onValidateBooking={(email) =>
          validateBookingGuest(
            {
              experienceId,
              selectedDate,
              userEmail: email,
              addOnIds: selectedAddOnIds,
              nofPersons: people,
              selectedStartTime: specificStartTime ? selectedStartTime : null,
            },
            {
              onSuccess: () => {
                setIsGuestModalOpen(false);
                setIsBookingModalOpen(true);
              },
            },
          )
        }
        email={email}
        setEmail={setEmail}
        isLoading={validatingGuest}
      />

      <ReviewBookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        experience={experience}
        validationData={token ? validationData : validationGuestData}
        people={people}
        email={email}
        successMessage={
          validationData?.success_message ||
          validationGuestData?.success_message
        }
        specificStartTime={specificStartTime}
      />
    </div>
  );
}
